import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { collection, doc, getDoc, getDocs, updateDoc, deleteDoc } from "firebase/firestore";
import { FiCheck, FiRotateCcw, FiTrash2 } from "react-icons/fi";
import { db } from "../firebase";

// ── helpers ──────────────────────────────────────────────────────
// questionReports.reportedAt is a Firestore Timestamp; reportederrors.reportedAt
// is a plain ISO string — normalize both to a JS Date here.
function toDate(value) {
    if (!value) return null;
    if (typeof value.toDate === "function") return value.toDate();
    if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDate(date) {
    if (!date) return "Unknown";
    return new Intl.DateTimeFormat("en-US", {
        day: "numeric", month: "short", year: "numeric",
        hour: "numeric", minute: "2-digit", hour12: true,
    }).format(date);
}

const SOURCE_META = {
    question: { label: "Question", color: "#3a82c6", shadow: "#317aa8" },
    flashcard: { label: "Flashcard Set", color: "#f4a261", shadow: "#dd8f50" },
};

const selectStyle = {
    fontFamily: "'Quicksand', sans-serif", fontSize: 14, fontWeight: 600,
    padding: "9px 12px", borderRadius: 10,
    border: "1.5px solid #E0E0E0", background: "#fff", color: "#000",
    cursor: "pointer", outline: "none", flex: "0 1 180px", minWidth: 150,
};

function Badge({ label, color, shadow }) {
    return (
        <span style={{
            display: "inline-block", background: color, color: "#fff",
            border: `1px solid ${shadow}`, borderRadius: 999,
            padding: "3px 10px", fontSize: 11, fontWeight: 700,
            textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap",
        }}>
            {label}
        </span>
    );
}

function StatusPill({ status }) {
    const resolved = status === "resolved";
    return (
        <span style={{
            display: "inline-block",
            background: resolved ? "#50C878" : "#e76f51", color: "#fff",
            borderRadius: 999, padding: "3px 10px",
            fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em",
        }}>
            {resolved ? "Resolved" : "Open"}
        </span>
    );
}

function ReporterCell({ reporterId, info }) {
    if (!reporterId) return <span style={{ color: "#aaa" }}>Unknown</span>;
    const label = info?.username ? `@${info.username}` : (info?.email || `${reporterId.slice(0, 8)}…`);
    return (
        <Link to={`/users/${reporterId}`} style={{ color: "#2a9d8f", fontWeight: 700, textDecoration: "none" }}>
            {label}
        </Link>
    );
}

function ReportDetails({ item, lessonTitles, setTitles }) {
    if (item.source === "question") {
        return (
            <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#000", marginBottom: 2 }}>
                    {item.questionText || "(no question text captured)"}
                </div>
                <div style={{ fontSize: 12, color: "#606060" }}>
                    {item.reason}
                    {item.questionType ? ` · ${item.questionType}` : ""}
                    {" · "}{lessonTitles[item.lessonId] || item.lessonId || "Unknown lesson"}
                    {typeof item.screenIndex === "number" ? ` (screen #${item.screenIndex})` : ""}
                </div>
                {item.message && (
                    <div style={{ fontSize: 12, color: "#333", marginTop: 4, fontStyle: "italic" }}>
                        “{item.message}”
                    </div>
                )}
            </div>
        );
    }
    return (
        <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#000", marginBottom: 2 }}>
                {setTitles[item.setId] || item.setId || "Unknown set"}
            </div>
            <div style={{ fontSize: 12, color: "#333" }}>{item.message}</div>
        </div>
    );
}

function IconBtn({ icon, color, border, onClick, title, disabled }) {
    return (
        <button
            title={title}
            onClick={onClick}
            disabled={disabled}
            style={{
                width: 32, height: 32, borderRadius: 9,
                border: `1.5px solid ${border}`, borderBottomWidth: 3,
                background: disabled ? "#eee" : color, color: "#fff",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                cursor: disabled ? "not-allowed" : "pointer", flexShrink: 0,
                opacity: disabled ? 0.5 : 1,
            }}
        >
            {icon}
        </button>
    );
}

// Where clicking a report should take the admin — the reported question's
// lesson (deep-linked to that screen) or the reported flashcard set.
function targetPathFor(item) {
    if (item.source === "question" && item.lessonId) {
        const q = typeof item.screenIndex === "number" ? `?q=${item.screenIndex}` : "";
        return `/lessons/${item.lessonId}/edit${q}`;
    }
    if (item.source === "flashcard" && item.setId) {
        return `/flashcards/${item.setId}/edit`;
    }
    return null;
}

// ── Reports page ─────────────────────────────────────────────────
function Reports() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [lessonTitles, setLessonTitles] = useState({});
    const [setTitles, setSetTitles] = useState({});
    const [reporterInfo, setReporterInfo] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [deleteError, setDeleteError] = useState("");
    const [sourceFilter, setSourceFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [busyKey, setBusyKey] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const [qSnap, fSnap] = await Promise.all([
                    getDocs(collection(db, "questionReports")),
                    getDocs(collection(db, "reportederrors")),
                ]);

                const questionItems = qSnap.docs.map(d => {
                    const data = d.data();
                    return {
                        id: d.id, source: "question",
                        reportedAt: toDate(data.reportedAt),
                        status: data.status === "resolved" ? "resolved" : "open",
                        reporterId: data.reporterId || "",
                        lessonId: data.lessonId || "",
                        screenIndex: data.screenIndex,
                        questionType: data.questionType || null,
                        questionText: data.questionText || "",
                        reason: data.reason || "",
                        message: data.message || "",
                    };
                });

                const flashcardItems = fSnap.docs.map(d => {
                    const data = d.data();
                    return {
                        id: d.id, source: "flashcard",
                        reportedAt: toDate(data.reportedAt),
                        status: data.status === "resolved" ? "resolved" : "open",
                        reporterId: data.userId || "",
                        setId: data.setId || "",
                        message: data.message || "",
                    };
                });

                const combined = [...questionItems, ...flashcardItems].sort(
                    (a, b) => (b.reportedAt?.getTime() || 0) - (a.reportedAt?.getTime() || 0)
                );
                setItems(combined);
                setLoading(false);

                // Resolve human-readable lesson/set titles and reporter identities.
                // Done as a second pass so the table can render before these land.
                const lessonIds = [...new Set(questionItems.map(i => i.lessonId).filter(Boolean))];
                const setIds = [...new Set(flashcardItems.map(i => i.setId).filter(Boolean))];
                const reporterIds = [...new Set(combined.map(i => i.reporterId).filter(Boolean))];

                const [lessonEntries, setEntries, reporterEntries] = await Promise.all([
                    Promise.all(lessonIds.map(async id => {
                        try {
                            const s = await getDoc(doc(db, "lessons", id));
                            return [id, s.exists() ? (s.data().title || "") : ""];
                        } catch { return [id, ""]; }
                    })),
                    Promise.all(setIds.map(async id => {
                        try {
                            const s = await getDoc(doc(db, "flashcards", id));
                            return [id, s.exists() ? (s.data().title || "") : ""];
                        } catch { return [id, ""]; }
                    })),
                    Promise.all(reporterIds.map(async id => {
                        try {
                            const s = await getDoc(doc(db, "users", id));
                            const d = s.exists() ? s.data() : {};
                            return [id, { username: d.username || "", email: d.email || "" }];
                        } catch { return [id, {}]; }
                    })),
                ]);

                setLessonTitles(Object.fromEntries(lessonEntries));
                setSetTitles(Object.fromEntries(setEntries));
                setReporterInfo(Object.fromEntries(reporterEntries));
            } catch (err) {
                setError("Unable to load content reports.");
                setLoading(false);
            }
        };
        load();
    }, []);

    const filtered = useMemo(() => items.filter(i =>
        (sourceFilter === "all" || i.source === sourceFilter) &&
        (statusFilter === "all" || i.status === statusFilter)
    ), [items, sourceFilter, statusFilter]);

    const openCount = items.filter(i => i.status === "open").length;
    const hasFilters = sourceFilter !== "all" || statusFilter !== "all";

    const collectionFor = (source) => (source === "question" ? "questionReports" : "reportederrors");

    const handleToggleStatus = async (item) => {
        const key = `${item.source}-${item.id}`;
        const nextStatus = item.status === "resolved" ? "open" : "resolved";
        setBusyKey(key);
        try {
            await updateDoc(doc(db, collectionFor(item.source), item.id), { status: nextStatus });
            setItems(prev => prev.map(i =>
                i.source === item.source && i.id === item.id ? { ...i, status: nextStatus } : i
            ));
        } finally {
            setBusyKey(null);
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm("Delete this report? This cannot be undone.")) return;
        const key = `${item.source}-${item.id}`;
        setBusyKey(key);
        setDeleteError("");
        try {
            await deleteDoc(doc(db, collectionFor(item.source), item.id));
            setItems(prev => prev.filter(i => !(i.source === item.source && i.id === item.id)));
        } catch (err) {
            setDeleteError(err?.code === "permission-denied"
                ? "Permission denied. Your CMS admin account needs delete access to reports."
                : `Delete failed: ${err.message}`);
        } finally {
            setBusyKey(null);
        }
    };

    if (loading) {
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh", gap: 12, color: "#606060", fontWeight: 600, fontFamily: "'Quicksand', sans-serif" }}>
                <div style={{ width: 20, height: 20, border: "2.5px solid #E0E0E0", borderTop: "2.5px solid #2a9d8f", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                Loading content reports…
            </div>
        );
    }

    return (
        <div style={{ fontFamily: "'Quicksand', sans-serif", maxWidth: 1100 }}>

            {/* ── Page header ── */}
            <div style={{ marginBottom: 24 }}>
                <h1 style={{ margin: 0 }}>Content Reports</h1>
                <div style={{ color: "#606060", fontWeight: 600, fontSize: 14, marginTop: 4 }}>
                    User-submitted mistake/error reports from lesson questions and flashcard sets
                </div>
            </div>

            {error && <p style={{ color: "#e76f51", fontWeight: 600 }}>{error}</p>}
            {deleteError && <p style={{ color: "#e76f51", fontWeight: 600 }}>{deleteError}</p>}

            {/* ── Stat cards ── */}
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
                <div style={{ flex: "1 1 180px", background: "#fff", borderRadius: 16, border: "1.5px solid #E0E0E0", borderBottom: "3px solid #E0E0E0", padding: "16px 20px" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#606060", marginBottom: 6 }}>Open Reports</div>
                    <div style={{ fontSize: 26, fontWeight: 800, color: openCount > 0 ? "#e76f51" : "#000" }}>{openCount}</div>
                </div>
                <div style={{ flex: "1 1 180px", background: "#fff", borderRadius: 16, border: "1.5px solid #E0E0E0", borderBottom: "3px solid #E0E0E0", padding: "16px 20px" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#606060", marginBottom: 6 }}>Total Reports</div>
                    <div style={{ fontSize: 26, fontWeight: 800 }}>{items.length}</div>
                </div>
            </div>

            {/* ── Filter bar ── */}
            <div style={{
                background: "#fff", borderRadius: 16, border: "1px solid #E0E0E0",
                padding: "14px 18px", marginBottom: 20,
                display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center",
            }}>
                <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} style={selectStyle}>
                    <option value="all">All Sources</option>
                    <option value="question">Question Reports</option>
                    <option value="flashcard">Flashcard Set Reports</option>
                </select>
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={selectStyle}>
                    <option value="all">All Statuses</option>
                    <option value="open">Open</option>
                    <option value="resolved">Resolved</option>
                </select>
                {hasFilters && (
                    <button
                        onClick={() => { setSourceFilter("all"); setStatusFilter("all"); }}
                        style={{
                            background: "none", border: "1.5px solid #E0E0E0", borderRadius: 10,
                            padding: "9px 14px", fontFamily: "'Quicksand', sans-serif",
                            fontWeight: 700, fontSize: 13, color: "#606060", cursor: "pointer",
                        }}
                    >
                        ✕ Clear
                    </button>
                )}
            </div>

            {/* ── Table ── */}
            {filtered.length === 0 ? (
                <div style={{
                    background: "#fff", borderRadius: 20, border: "1.5px dashed #E0E0E0",
                    padding: "48px 32px", textAlign: "center", color: "#aaa", fontWeight: 600, fontSize: 15,
                }}>
                    {hasFilters ? "No reports match your filters." : "No content reports yet."}
                </div>
            ) : (
                <div style={{ overflowX: "auto" }}>
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Source</th>
                                <th>Reported</th>
                                <th>Reporter</th>
                                <th>Details</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(item => {
                                const key = `${item.source}-${item.id}`;
                                const meta = SOURCE_META[item.source];
                                const busy = busyKey === key;
                                const targetPath = targetPathFor(item);
                                return (
                                    <tr
                                        key={key}
                                        onClick={targetPath ? () => navigate(targetPath) : undefined}
                                        title={targetPath ? `Go to reported ${item.source === "question" ? "question" : "flashcard set"}` : undefined}
                                        style={{ cursor: targetPath ? "pointer" : "default" }}
                                    >
                                        <td><Badge label={meta.label} color={meta.color} shadow={meta.shadow} /></td>
                                        <td style={{ whiteSpace: "nowrap" }}>{formatDate(item.reportedAt)}</td>
                                        <td onClick={e => e.stopPropagation()}>
                                            <ReporterCell reporterId={item.reporterId} info={reporterInfo[item.reporterId]} />
                                        </td>
                                        <td style={{ maxWidth: 420 }}>
                                            <ReportDetails item={item} lessonTitles={lessonTitles} setTitles={setTitles} />
                                        </td>
                                        <td><StatusPill status={item.status} /></td>
                                        <td onClick={e => e.stopPropagation()}>
                                            <div style={{ display: "flex", gap: 6 }}>
                                                <IconBtn
                                                    icon={item.status === "resolved" ? <FiRotateCcw size={13} /> : <FiCheck size={13} />}
                                                    color={item.status === "resolved" ? "#e9c46a" : "#50C878"}
                                                    border={item.status === "resolved" ? "#d4b15f" : "#46B06A"}
                                                    title={item.status === "resolved" ? "Reopen" : "Mark resolved"}
                                                    disabled={busy}
                                                    onClick={() => handleToggleStatus(item)}
                                                />
                                                <IconBtn
                                                    icon={<FiTrash2 size={13} />}
                                                    color="#e76f51" border="#cf6348"
                                                    title="Delete report"
                                                    disabled={busy}
                                                    onClick={() => handleDelete(item)}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default Reports;
